import { Check, Copy, Link2, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

/** Cobrança Pix (pedido ou fechamento): QR, copia e cola e link público para enviar. */
export function PixChargeModal({ charge, onClose }) {
  const [copied, setCopied] = useState("");
  const link = `${window.location.origin}${charge.path}`;

  const copy = async (what, text) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    toast.success(what === "link" ? "Link de pagamento copiado." : "Código Pix copiado.");
    setTimeout(() => setCopied(""), 2500);
  };

  const whatsapp = () => {
    const text = (charge.message_template || "{link}").replace("{link}", link);
    const base = charge.phone ? `https://wa.me/${charge.phone}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Cobrança Pix"
      description="Envie o link da página de pagamento. A confirmação do pagamento continua sendo manual."
      testid="pix-charge-modal"
    >
      <div className="mt-2 w-full min-w-0 space-y-4">
        <div className="rounded-xl border border-white/10 bg-secondary/40 p-4 text-center">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {charge.origin_label} {charge.label}
          </p>
          {charge.order?.discount_amount > 0 && (
            <div className="mt-2 space-y-0.5 text-xs" data-testid="pix-charge-discount">
              <p className="text-muted-foreground">
                Subtotal: <span className="font-num">{brl(charge.order.subtotal_original)}</span>
              </p>
              <p className="text-amber-300">
                Desconto:{" "}
                <span className="font-num">
                  {charge.order.discount_type === "PERCENTUAL"
                    ? `${charge.order.discount_value}% (- ${brl(charge.order.discount_amount)})`
                    : `- ${brl(charge.order.discount_amount)}`}
                </span>
              </p>
            </div>
          )}
          <p className="mt-1 font-num text-3xl text-brand" data-testid="pix-charge-amount">
            {brl(charge.amount)}
          </p>
          <img
            src={charge.qr_code}
            alt="QR Code Pix"
            className="mx-auto mt-4 h-44 w-44 rounded-lg bg-white p-2"
            data-testid="pix-charge-qr"
          />
        </div>

        {charge.declared_at && (
          <p className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-xs text-sky-200">
            Cliente informou que realizou o pagamento. Confira e use "Confirmar pagamento".
          </p>
        )}

        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Pix Copia e Cola</p>
          <p
            className="mt-1.5 max-h-24 overflow-y-auto break-all rounded-lg border border-white/10 bg-secondary/40 p-3 text-[11px] text-muted-foreground"
            data-testid="pix-charge-brcode"
          >
            {charge.brcode}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => copy("code", charge.brcode)}
            data-testid="pix-copy-code-btn"
            className="h-10 gap-1.5 rounded-full text-xs"
          >
            {copied === "code" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            Copiar código
          </Button>
          <Button
            variant="outline"
            onClick={() => copy("link", link)}
            data-testid="pix-copy-link-btn"
            className="h-10 gap-1.5 rounded-full text-xs"
          >
            {copied === "link" ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
            Copiar link
          </Button>
          <Button
            onClick={whatsapp}
            data-testid="pix-whatsapp-btn"
            className="h-10 gap-1.5 rounded-full text-xs font-semibold"
          >
            <Send className="h-3.5 w-3.5" /> Enviar no WhatsApp
          </Button>
        </div>
        <p className="break-all text-[11px] text-muted-foreground">{link}</p>
      </div>
    </ResponsiveModal>
  );
}
